// src/components/partners/PartnersSectionShadcn.jsx
import React, { useState } from 'react';
import {
  Search,
  Inbox,
  UserCheck,
  Settings
} from 'lucide-react';

// shadcn components
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';

// Import shadcn navigation components
import TabNavigationShadcn from '../ui/navigation/TabNavigation/TabNavigationShadcn';

// Import NEW shadcn tab components
import DirectoryTabShadcn from './tabs/DirectoryTabShadcn';
import PartnersTabShadcn from './tabs/PartnersTabShadcn';
import RequestsTabShadcn from './tabs/RequestsTabShadcn';
import ProfileTabShadcn from './tabs/ProfileTabShadcn';

import { cn } from '../../utils/cn';

// Import the real partners hook
import { usePartners } from '../../hooks/usePartners';

const PartnersSectionShadcn = ({ className = '' }) => {
  // Use real partners hook
  const {
    // Data state
    connectedPartners,
    pendingConnections,
    networkProfile,
    networkStats,
    searchResults,

    // Loading states
    partnersLoading,
    searchLoading,
    connectionLoading,
    partnersError,

    // Operations
    loadPartnerNetwork,
    searchPartnerBusinesses,
    sendConnectionRequest,
    respondToConnectionRequest,
    updateNetworkProfile,

    // Utilities
    refreshPartnerNetwork,
    clearSearchResults,
    clearPartnersError,
    isConnected
  } = usePartners();

  // Local state only for UI concerns
  const [activeTab, setActiveTab] = useState('partners');

  // Calculate enhanced network stats from real data
  const enhancedNetworkStats = React.useMemo(() => {
    if (!connectedPartners && !pendingConnections) {
      return {
        totalConnections: 0,
        pendingRequests: 0,
        activePartners: 0,
        thisWeekBookings: 0
      };
    }

    // Handle different pendingConnections structures
    const sentConnections = Array.isArray(pendingConnections) ?
      pendingConnections.filter(c => c.type === 'sent') :
      (pendingConnections.sent || []);

    const receivedConnections = Array.isArray(pendingConnections) ?
      pendingConnections.filter(c => c.type === 'received') :
      (pendingConnections.received || []);

    return {
      totalConnections: (connectedPartners?.length || 0) + sentConnections.length + receivedConnections.length,
      pendingRequests: sentConnections.length + receivedConnections.length,
      activePartners: connectedPartners?.length || 0,
      thisWeekBookings: networkStats?.thisWeekBookings || 0
    };
  }, [connectedPartners, pendingConnections, networkStats]);

  // Tab configuration with Partners/Requests separation
  const tabs = [
    {
      id: 'partners',
      label: 'Partners',
      icon: UserCheck,
      color: 'emerald',
      colorRgb: '16 185 129'
    },
    {
      id: 'requests',
      label: 'Requests',
      icon: Inbox,
      color: 'purple',
      colorRgb: '147 51 234'
    },
    {
      id: 'directory',
      label: 'Directory',
      icon: Search,
      color: 'orange',
      colorRgb: '249 115 22'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: Settings,
      color: 'slate',
      colorRgb: '100 116 139'
    }
  ];

  // Real search operation
  const handleSearch = async (searchTerm) => {
    console.log('🔍 Searching for partners:', searchTerm);

    try {
      clearPartnersError();

      searchPartnerBusinesses({
        search_term: searchTerm,
        business_types: [],
        location_radius: 50,
        include_contact_info: true
      });

      console.log('✅ Partner search request submitted');

    } catch (err) {
      console.error('Search error:', err);
    }
  };

  // Real connection request operation
  const handleSendRequest = async (businessId, note) => {
    console.log('📤 Sending real connection request to:', businessId);

    try {
      clearPartnersError();

      sendConnectionRequest({
        target_business_id: businessId,
        connection_note: note,
        connection_type: 'partnership'
      });

      console.log('✅ Connection request submitted');

    } catch (err) {
      console.error('Send request error:', err);
    }
  };

  // Real connection response operations
  const handleAcceptConnection = async (connectionId, businessName) => {
    console.log('✅ Accepting real connection:', connectionId);

    try {
      clearPartnersError();

      respondToConnectionRequest({
        request_id: connectionId,
        response: 'accept',
        notes: `Welcome to our partner network, ${businessName}!`
      });

      console.log('✅ Connection acceptance submitted');

    } catch (err) {
      console.error('Accept connection error:', err);
    }
  };

  const handleRejectConnection = async (connectionId, businessName) => {
    console.log('❌ Rejecting real connection:', connectionId);

    try {
      clearPartnersError();

      respondToConnectionRequest({
        request_id: connectionId,
        response: 'decline',
        notes: 'Thank you for your interest, but we are not able to partner at this time.'
      });

      console.log('✅ Connection rejection submitted');

    } catch (err) {
      console.error('Reject connection error:', err);
    }
  };

  // Show loading screen on initial load
  if (partnersLoading && !connectedPartners && !pendingConnections) {
    return (
      <div className={cn("container mx-auto p-6", className)}>
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-muted-foreground">
            <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin"></div>
            <span>Loading partner network...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("container mx-auto p-6", className)}>
      {/* Global Error Alert */}
      {partnersError && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center justify-between">
            <span>{partnersError}</span>
            <Button variant="ghost" size="sm" onClick={clearPartnersError}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Tab Navigation */}
      <div className="mb-8">
        <TabNavigationShadcn
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* Tab Content */}
      <div className="pb-8">
        {activeTab === 'partners' && (
          <PartnersTabShadcn
            connectedPartners={connectedPartners}
            networkStats={enhancedNetworkStats}
            loading={partnersLoading}
            isConnected={isConnected}
            onRefresh={refreshPartnerNetwork}
          />
        )}

        {activeTab === 'requests' && (
          <RequestsTabShadcn
            pendingConnections={pendingConnections}
            connectionLoading={connectionLoading}
            isConnected={isConnected}
            onAcceptConnection={handleAcceptConnection}
            onRejectConnection={handleRejectConnection}
            onRefresh={refreshPartnerNetwork}
          />
        )}

        {activeTab === 'directory' && (
          <DirectoryTabShadcn
            searchResults={searchResults}
            searchLoading={searchLoading}
            isConnected={isConnected}
            onSearch={handleSearch}
            onSendRequest={handleSendRequest}
            onClearSearch={clearSearchResults}
          />
        )}

        {activeTab === 'profile' && (
          <ProfileTabShadcn
            networkProfile={networkProfile}
            partnersError={partnersError}
            isConnected={isConnected}
            onProfileUpdate={updateNetworkProfile}
          />
        )}
      </div>
    </div>
  );
};

export default PartnersSectionShadcn;